import * as React from 'react'
import * as ReactDOM from 'react-dom'

type State = 'mounted' | 'entering' | 'visible' | 'exiting' | 'unmounted'
type Action = 'MOUNT' | 'ENTER' | 'EXIT' | 'TRANSITION_END' | 'UNMOUNT'

/**
 * Single-class transitioning hook.
 *
 * Step 1: add a class style (`.hidden` works) with your usual hidden styles like opacity 0 + a transition for opacity.
 * Step 2: use hook
 *
 * @param showing The prop controlling whether the element is currently showing.
 * @example
 *
 *   const [ref, rendering, hidden] = useCssTransition(props.showing)
 *   return (
 *     rendering && <div className={cx(styles.root, hidden && styles.hidden)} ref={ref} />
 *   )`
 */
export function useCssTransition(
  showing: boolean,
): [React.RefCallback<HTMLElement>, true | null, boolean] {
  const initialState = showing ? 'mounted' : 'unmounted'
  const [state, setState] = React.useState<State>(initialState)

  const [node, setNode] = React.useState<HTMLElement>()

  const ref = React.useCallback((node: HTMLElement) => {
    setNode(node)
  }, [])

  const send = React.useCallback(
    (action: Action) =>
      setState(
        (currentState) => transitionState(currentState, action) ?? currentState,
      ),
    [setState],
  )

  // Handle changes to the source indicating whether we are showing.
  React.useLayoutEffect(() => {
    send(showing ? 'MOUNT' : 'EXIT')
  }, [showing, send])

  // Sometimes transition events aren't triggered during tests due to how
  // quick stuff happens. As an insurance we'll make sure that we end up in
  // the unmounted state eventually using a timer.
  React.useEffect(() => {
    if (state === 'exiting') {
      // No transition should run for more than 500ms.
      const timeout = setTimeout(() => {
        send('UNMOUNT')
      }, 500)
      return () => clearTimeout(timeout)
    }
  }, [state])

  // Subscribe to transition events to notify of state changes.
  React.useLayoutEffect(() => {
    if (!node) {
      // Transition to the unmounted state if the node is removed prematurely.
      // We avoid doing so during cleanup as the node may change but still exist.
      send('TRANSITION_END')
      return
    }

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === node) {
        // With React 18 concurrency this update is applied
        // a frame after the transition ends, creating a flash
        // of visible content. By manually flushing we ensure they sync within
        // a frame, removing the flash.
        ReactDOM.flushSync(() => send('TRANSITION_END'))
      }
    }

    node.addEventListener('transitioncancel', handleTransitionEnd)
    node.addEventListener('transitionend', handleTransitionEnd)

    // Once we have a node, we're considered mounted,
    // so trigger the `ENTER` transition.
    send('ENTER')

    return () => {
      node.removeEventListener('transitioncancel', handleTransitionEnd)
      node.removeEventListener('transitionend', handleTransitionEnd)
    }
  }, [node, send])

  const rendering = state !== 'unmounted'
  const hidden = state !== 'visible' && state !== 'entering'

  return [ref, rendering ? true : null, hidden]
}

function transitionState(state: State, action: Action): State | null {
  switch (state) {
    case 'mounted': {
      switch (action) {
        case 'ENTER':
          return 'entering'
        case 'UNMOUNT':
          return 'unmounted'
        case 'EXIT':
          return 'exiting'
      }
      break
    }
    case 'entering': {
      switch (action) {
        case 'TRANSITION_END':
          return 'visible'
        case 'UNMOUNT':
          return 'unmounted'
        case 'EXIT':
          return 'exiting'
      }
      break
    }
    case 'visible': {
      switch (action) {
        case 'EXIT':
          return 'exiting'
        case 'UNMOUNT':
          return 'unmounted'
      }
      break
    }
    case 'exiting': {
      switch (action) {
        case 'MOUNT':
          return 'visible'
        case 'ENTER':
          return 'entering'
        case 'TRANSITION_END':
        case 'UNMOUNT':
          return 'unmounted'
      }
      break
    }
    case 'unmounted': {
      switch (action) {
        case 'MOUNT':
          return 'mounted'
      }
    }
  }

  return null
}
